import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/authContext';
import LayoutAdmin from '../components/layout/LayoutAdmin';
import { 
  FaUsers, 
  FaCheckCircle, 
  FaShieldAlt, 
  FaPlus, 
  FaChartBar, 
  FaCog,
  FaTimesCircle
} from 'react-icons/fa';

const SuperAdminDashboard = () => {
  const { tokens } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API base URL from environment variables
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const fetchSystemStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/admin/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokens.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setStats(result.data.stats);
    } catch (err) {
      console.error('Error fetching system stats:', err);
      setError(err.message || 'Failed to fetch system statistics');
    } finally {
      setLoading(false);
    }
  }, [tokens.accessToken, API_BASE_URL]);

  useEffect(() => {
    fetchSystemStats();
  }, [fetchSystemStats]);

  const StatCard = ({ title, value, subtitle, icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'border-blue-500',
      green: 'border-green-500',
      purple: 'border-purple-500',
      red: 'border-red-500',
      yellow: 'border-yellow-500'
    };
    
    const iconColorClasses = {
      blue: 'text-blue-500',
      green: 'text-green-500',
      purple: 'text-purple-500',
      red: 'text-red-500',
      yellow: 'text-yellow-500'
    };

    return (
      <div className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${colorClasses[color] || colorClasses.blue}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
          <div className={`${iconColorClasses[color] || iconColorClasses.blue} text-3xl`}>
            {icon}
          </div>
        </div>
      </div>
    );
  };

  const RoleStatsCard = ({ roleStats }) => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Users by Role</h3>
      <div className="space-y-3">
        {Object.entries(roleStats).map(([role, count]) => (
          <div key={role} className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-600 capitalize">
              {role.replace('_', ' ')}
            </span>
            <span className="text-lg font-semibold text-gray-900">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <LayoutAdmin>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </LayoutAdmin>
    );
  }

  if (error) {
    return (
      <LayoutAdmin>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaTimesCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading dashboard</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={fetchSystemStats}
                className="mt-2 text-sm text-red-800 hover:text-red-900 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </LayoutAdmin>
    );
  }

  return (
    <LayoutAdmin>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Super Admin Dashboard</h1>
            <p className="text-gray-600 mt-1">
              System overview and statistics
              {stats?.lastUpdated && (
                <span className="ml-2 text-sm text-gray-500">
                  Last updated: {new Date(stats.lastUpdated).toLocaleString()}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Users"
            value={stats?.totalUsers || 0}
            subtitle="All registered users"
            icon={<FaUsers />}
            color="blue"
          />
          <StatCard
            title="Active Users"
            value={stats?.activeUsers || 0}
            subtitle="Currently active accounts"
            icon={<FaCheckCircle />}
            color="green"
          />
          <StatCard
            title="Verified Users"
            value={stats?.verifiedUsers || 0}
            subtitle="Email verified accounts"
            icon={<FaShieldAlt />}
            color="purple"
          />
        </div>

        {/* Role Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RoleStatsCard roleStats={stats?.userCountsByRole || {}} />
          
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full text-left p-3 rounded-md border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors">
                <div className="flex items-center space-x-3">
                  <FaPlus className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">Create New Admin</span>
                </div>
              </button>
              <button className="w-full text-left p-3 rounded-md border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-colors">
                <div className="flex items-center space-x-3">
                  <FaChartBar className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">View Reports</span>
                </div>
              </button>
              <button className="w-full text-left p-3 rounded-md border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors">
                <div className="flex items-center space-x-3">
                  <FaCog className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">System Settings</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </LayoutAdmin>
  );
};

export default SuperAdminDashboard;